/**
 * The public program pass-rate data behind /programs and /programs/:state/:slug.
 *
 * index.json carries the card fields for every school and rides the index
 * chunk. The per-school records are 5.3MB together, so a school page reaches its
 * own through schoolRecord.ts and downloads nothing else.
 *
 * NULLS ARE NULL. A category with no examinations stays null all the way to the
 * page. "No one sat it" and "everybody failed" are different facts.
 *
 * RELATIVE imports on purpose: prerenderRoutes.ts pulls this module through
 * Vite's esbuild config loader, which does not know the app's path aliases.
 */
import indexData from '../../data/programs/index.json';
import { RANKABLE_MIN_EXAMS } from './floor';
import { JURISDICTIONS } from '../licensing/jurisdictions';

export interface ProgramRate {
  exams: number;
  percent: number;
  /** National first-time rate minus this school's, in points. Positive sits BELOW national. */
  gap: number;
}

export interface ProgramSummary {
  schoolCode: string;
  name: string;
  state: string;
  slug: string;
  masters: ProgramRate | null;
  clinical: ProgramRate | null;
  ipeds: ProgramIpeds | null;
}

export interface ProgramRow {
  exam: string;
  group: string;
  exams: number | null;
  passed: number | null;
  percent: number | null;
  jurisdictionRate: number | null;
  nationalRate: number | null;
}

export interface ProgramAccreditation {
  level: string;
  status: string;
  url: string | null;
}

/** The NCES IPEDS record matched to this school. null when no institution matched its name. */
export interface ProgramIpeds {
  unitId: string;
  hbcu: boolean;
  /** 1 public, 2 private nonprofit, 3 private for-profit. */
  control: number;
  tribal: boolean;
  degreesConferred: { bachelors: number; masters: number };
  year: number;
}

export interface ProgramSource {
  name: string;
  url: string;
  lastChecked: string;
}

export interface SchoolRecord {
  schoolCode: string;
  name: string;
  state: string;
  slug: string;
  examYear: number;
  source: ProgramSource;
  masters: ProgramRate | null;
  clinical: ProgramRate | null;
  rows: ProgramRow[];
  accreditation: ProgramAccreditation[];
  ipeds: ProgramIpeds | null;
  /**
   * First-time ASWB Masters examinations per IPEDS Masters graduate. A
   * prospecting figure and a join check, not a claim about a school: it stays
   * out of the page copy.
   */
  examineesPerGraduate: { masters: number | null };
  /** The previous edition's first-time lines. null when the 2024 figures were not extracted for the school. */
  priorYear: {
    examYear: number;
    masters: { exams: number; percent: number | null } | null;
    clinical: { exams: number; percent: number | null } | null;
  } | null;
}

const index = indexData as unknown as {
  examYear: number;
  ipedsYear: number;
  source: ProgramSource;
  schools: ProgramSummary[];
};

export const PROGRAMS_EXAM_YEAR = index.examYear;
export const PROGRAMS_IPEDS_YEAR = index.ipedsYear;
export const PROGRAMS_SOURCE = index.source;
export const PROGRAMS_LAST_CHECKED = index.source.lastChecked;

/** "September 11, 2026". A date a person reads is written the way a person writes it. */
export function longDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export const PROGRAMS_CHECKED_ON = longDate(PROGRAMS_LAST_CHECKED);

/** The one sentence every program page carries. Never "verified". */
export const CHECKED_LINE = `Checked against ASWB's published school report. Last checked ${PROGRAMS_CHECKED_ON}.`;

/** Every figure on a program page, with the year and the body that published it. */
export const SOURCES_LINE =
  `Sources: ASWB exam pass rates for ${index.examYear}, the CSWE program directory, `
  + `and NCES IPEDS ${index.ipedsYear}.`;

/** The HBCU line, said as IPEDS says it and dated to the file it came from. */
export function hbcuLine(ipeds: ProgramIpeds | null): string | null {
  return ipeds?.hbcu ? `Historically Black College or University (per IPEDS ${ipeds.year})` : null;
}

export const NOT_AFFILIATED =
  'Social Work Test Prep Academy is not affiliated with ASWB, CSWE or any school. Pass rates are read off the school report ASWB publishes for the exam year; accreditation comes from the CSWE program directory.';

export function allSchools(): readonly ProgramSummary[] {
  return index.schools;
}

export function schoolUrl(school: { state: string; slug: string }): string {
  return `/programs/${school.state.toLowerCase()}/${school.slug}`;
}

const STATE_NAMES = new Map(JURISDICTIONS.map((j) => [j.code, j.name]));

export function stateName(code: string): string {
  return STATE_NAMES.get(code.toUpperCase()) ?? code.toUpperCase();
}

/** One jurisdiction's programs, name-ordered. Empty for a code ASWB reports none for. */
export function programsInState(code: string): ProgramSummary[] {
  const state = code.toUpperCase();
  return index.schools
    .filter((school) => school.state === state)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function findSchoolByCode(schoolCode: string): ProgramSummary | null {
  return index.schools.find((s) => s.schoolCode === schoolCode) ?? null;
}

export function findSchool(state: string, slug: string): ProgramSummary | null {
  const code = state.toUpperCase();
  return index.schools.find((s) => s.state === code && s.slug === slug) ?? null;
}

/**
 * The national first-time rate, recovered from any school that reports the exam:
 * gap is national minus school, so school + gap is national. Resolved once, not
 * per card.
 */
const NATIONAL_RATES: Record<'masters' | 'clinical', number | null> = {
  masters: recoverNationalRate('masters'),
  clinical: recoverNationalRate('clinical'),
};

function recoverNationalRate(exam: 'masters' | 'clinical'): number | null {
  const rate = index.schools.find((s) => s[exam] !== null)?.[exam];
  return rate ? Math.round((rate.percent + rate.gap) * 10) / 10 : null;
}

export function nationalRate(exam: 'masters' | 'clinical'): number | null {
  return NATIONAL_RATES[exam];
}

/**
 * Accents, apostrophes, punctuation and the Saint/St. split all fold away, so
 * "St. John's", "Saint Johns" and "st johns" are one query against one school.
 */
export function foldSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[''\u2019]/g, '')
    .replace(/\bst\.?\b/g, 'saint')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const FOLDED_STATE = new Map(JURISDICTIONS.map((j) => [j.code, foldSearch(j.name)]));
const FOLDED_SCHOOL = new Map(
  index.schools.map((s) => [s.schoolCode, foldSearch(`${s.name} ${stateName(s.state)}`)]),
);

function foldedState(code: string): string {
  return FOLDED_STATE.get(code.toUpperCase()) ?? foldSearch(stateName(code));
}

function foldedSchool(school: ProgramSummary): string {
  return (
    FOLDED_SCHOOL.get(school.schoolCode) ?? foldSearch(`${school.name} ${stateName(school.state)}`)
  );
}

export function filterSchools(
  schools: readonly ProgramSummary[],
  query: string,
): ProgramSummary[] {
  const needle = foldSearch(query);
  if (!needle) return [...schools];
  // A two-letter query is a state code, not a substring: "md" matching the "md"
  // inside "Rutgers - Camden" is noise, not a search result.
  const byName = needle.length > 2;
  return schools.filter(
    (s) =>
      s.state.toLowerCase() === needle ||
      (byName ? foldedSchool(s) : foldedState(s.state)).includes(needle),
  );
}

export { RANKABLE_MIN_EXAMS } from './floor';

export function isReadable(rate: ProgramRate | null): boolean {
  return rate !== null && rate.exams >= RANKABLE_MIN_EXAMS;
}

export function isRankable(school: ProgramSummary, exam: ExamKey = 'clinical'): boolean {
  return isReadable(school[exam]);
}

/** Furthest from the national first-time rate first, either direction; floor-bound schools last. */
export function byGap(schools: readonly ProgramSummary[], exam: ExamKey): ProgramSummary[] {
  return [...schools].sort((a, b) => {
    const rankableA = isRankable(a, exam);
    const rankableB = isRankable(b, exam);
    if (rankableA !== rankableB) return rankableA ? -1 : 1;
    if (rankableA && rankableB) {
      const points = Math.abs(b[exam]?.gap ?? 0) - Math.abs(a[exam]?.gap ?? 0);
      if (points !== 0) return points;
    }
    return a.name.localeCompare(b.name);
  });
}

export function byClinicalGap(schools: readonly ProgramSummary[]): ProgramSummary[] {
  return byGap(schools, 'clinical');
}

export interface StateGroup {
  code: string;
  name: string;
  schools: ProgramSummary[];
}

export function byState(schools: readonly ProgramSummary[]): StateGroup[] {
  const groups = new Map<string, ProgramSummary[]>();
  for (const school of schools) {
    const list = groups.get(school.state) ?? [];
    list.push(school);
    groups.set(school.state, list);
  }
  return [...groups.entries()]
    .map(([code, list]) => ({
      code,
      name: stateName(code),
      schools: [...list].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type ExamKey = 'masters' | 'clinical';

const EXAM_LABEL: Record<ExamKey, string> = { masters: 'Masters', clinical: 'Clinical' };

/** "3 first-time Clinical candidates", singular when it is one person. */
export function candidateCount(exam: ExamKey, exams: number): string {
  return `${exams} first-time ${EXAM_LABEL[exam]} candidate${exams === 1 ? '' : 's'}`;
}

/**
 * What a sub-floor rate is allowed to say. Not the count either: a cohort of one
 * is the school's smallest graduating class, printed under its name. The bar in
 * FloorProgress carries how far along it is, without a headline number.
 */
export const FLOOR_LINE = 'Too few to rate so far';

/** The same fact in a sentence, for the schema description and the page copy. */
export const FLOOR_SENTENCE = 'too few first-time exams to rate so far';

function smallSample(exam: ExamKey): string {
  const national = nationalRate(exam);
  const tail = national === null ? '' : ` National first-time rate: ${national}%.`;
  return `Too few first-time ${EXAM_LABEL[exam]} exams in ${PROGRAMS_EXAM_YEAR} to rate so far.${tail}`;
}

/** The index card's line: the percent with the cohort behind it, or the honest blank. */
export function rateSummary(exam: ExamKey, rate: ProgramRate | null): string {
  if (rate === null) return 'No exams reported';
  if (!isReadable(rate)) return FLOOR_LINE;
  return `${rate.percent}% of ${rate.exams}`;
}

/** The school page's headline figure. A percent ONLY when enough people sat it. */
export function rateHeadline(rate: ProgramRate | null): string {
  if (rate === null) return 'No exams reported';
  if (!isReadable(rate)) return FLOOR_LINE;
  return `${rate.percent}%`;
}

/** One line in the house voice about where this school's Clinical first-timers landed. */
export function clinicalLine(school: ProgramSummary): string {
  const clinical = school.clinical;
  if (clinical === null) {
    return `No Clinical first-time exams in the ${PROGRAMS_EXAM_YEAR} report for this school.`;
  }
  if (!isReadable(clinical)) {
    return `Too few first-time Clinical exams in ${PROGRAMS_EXAM_YEAR} to rate so far.`;
  }
  const national = nationalRate('clinical');
  return `${clinical.percent}% of Clinical first-timers from here passed in ${PROGRAMS_EXAM_YEAR}.`
    + (national === null ? '' : ` National rate ${national}%.`);
}

/**
 * The year-over-year line beside a rate: "Up 5.6 points from 2024 (50.0%)", or
 * the prior figure alone when it did not rise. Only when both years clear the
 * floor, otherwise nothing: a trend off a handful of people is noise.
 */
export function trendLine(exam: ExamKey, record: Pick<SchoolRecord, ExamKey | 'priorYear'>): string | null {
  const now = record[exam];
  const then = record.priorYear?.[exam] ?? null;
  if (!isReadable(now) || then === null || then.percent === null || then.exams < RANKABLE_MIN_EXAMS) {
    return null;
  }
  const year = record.priorYear!.examYear;
  const was = `${then.percent.toFixed(1)}%`;
  const points = Math.round((now!.percent - then.percent) * 10) / 10;
  return points > 0 ? `Up ${points.toFixed(1)} points from ${year} (${was})` : `${year}: ${was}`;
}

/**
 * What a rate means for one graduate, said plainly: the school's own rate first,
 * the national rate beside it as context, never a verdict on the reader.
 */
export function readingFor(exam: ExamKey, rate: ProgramRate | null): string {
  const label = EXAM_LABEL[exam];
  const national = nationalRate(exam);
  if (rate === null || national === null) {
    return `No ${label} first-time exams are reported for this school in the ${PROGRAMS_EXAM_YEAR} report, so there is no rate to read. That is not a zero.`;
  }
  if (!isReadable(rate)) {
    return smallSample(exam);
  }
  const people = candidateCount(exam, rate.exams);
  return `${people} sat the ${label} exam in ${PROGRAMS_EXAM_YEAR} and ${rate.percent}% passed on the first try. The national first-time rate was ${national}%. A cohort rate is a starting line, not a verdict on you, and your own result comes from your own practice. See where you stand so your study time goes where it counts.`;
}

/**
 * The free diagnostic, with the school carried through to signup. `school_id` is
 * the partnership-tracking param the attribution store already keys on, so the
 * code rides to createTrialUser on the path that exists.
 */
export function tryUrlFor(schoolCode: string): string {
  return `/try?utm_source=programs&utm_medium=site&utm_campaign=programs_public&school_id=${schoolCode}`;
}

export type ProgramSort = 'gap' | 'name' | 'rate' | 'exams';

export interface ProgramQuery {
  q: string;
  state: string;
  exam: ExamKey;
  rated: boolean;
  hbcu: boolean;
  sort: ProgramSort;
  page: number;
}

export const PROGRAM_QUERY_DEFAULTS: ProgramQuery = {
  q: '',
  state: '',
  exam: 'clinical',
  rated: false,
  hbcu: false,
  sort: 'name',
  page: 1,
};

export const PROGRAM_SORTS: { value: ProgramSort; label: string }[] = [
  { value: 'name', label: 'School name' },
  { value: 'gap', label: 'Distance from national rate' },
  { value: 'rate', label: 'Highest first-time rate' },
  { value: 'exams', label: 'Most first-time exams' },
];

export function sortPrograms(
  schools: readonly ProgramSummary[],
  sort: ProgramSort,
  exam: ExamKey,
): ProgramSummary[] {
  if (sort === 'gap') return byGap(schools, exam);
  if (sort === 'name') return [...schools].sort((a, b) => a.name.localeCompare(b.name));
  if (sort === 'exams') {
    return [...schools].sort(
      (a, b) => (b[exam]?.exams ?? -1) - (a[exam]?.exams ?? -1) || a.name.localeCompare(b.name),
    );
  }
  // Rate order runs through the same floor as the gap order: a 0% off one
  // candidate is not the worst program in the country, so it sorts with the
  // unreadable ones rather than against the schools a rate can be read from.
  return [...schools].sort((a, b) => {
    const readableA = isReadable(a[exam]);
    const readableB = isReadable(b[exam]);
    if (readableA !== readableB) return readableA ? -1 : 1;
    if (readableA && readableB) {
      const points = (b[exam]?.percent ?? 0) - (a[exam]?.percent ?? 0);
      if (points !== 0) return points;
    }
    return a.name.localeCompare(b.name);
  });
}

export function selectPrograms(
  schools: readonly ProgramSummary[],
  query: ProgramQuery,
): ProgramSummary[] {
  let list = filterSchools(schools, query.q);
  const state = query.state.toUpperCase();
  if (state) list = list.filter((s) => s.state === state);
  if (query.rated) list = list.filter((s) => isReadable(s[query.exam]));
  if (query.hbcu) list = list.filter((s) => s.ipeds?.hbcu === true);
  return sortPrograms(list, query.sort, query.exam);
}

export const PROGRAMS_PAGE_SIZE = 25;

/** The /programs count line; empty at 0 because the empty state already says so. */
export function programsCountLabel(matches: number, total: number): string {
  if (matches === total) return `${total} programs`;
  if (matches === 0) return '';
  return matches === 1 ? '1 program matches' : `${matches} programs match`;
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / PROGRAMS_PAGE_SIZE));
}

/** A page number from anywhere (a URL, a stale click) landed inside the real range. */
export function clampPage(page: number, total: number): number {
  return Math.min(Math.max(1, Math.floor(page) || 1), pageCount(total));
}

export function pageItems<T>(items: readonly T[], page: number): T[] {
  const start = (clampPage(page, items.length) - 1) * PROGRAMS_PAGE_SIZE;
  return items.slice(start, start + PROGRAMS_PAGE_SIZE);
}

/** The pager's buttons: 1 … 12 13 14 … 27. 'gap' is an ellipsis, not a page. */
export function pageNumbers(page: number, pages: number): (number | 'gap')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const around = [page - 1, page, page + 1].filter((n) => n > 1 && n < pages);
  return [
    1,
    ...(around[0] > 2 ? (['gap'] as const) : []),
    ...around,
    ...(around[around.length - 1] < pages - 1 ? (['gap'] as const) : []),
    pages,
  ];
}

function isSort(value: string | null): value is ProgramSort {
  return PROGRAM_SORTS.some((s) => s.value === value);
}

/** The URL is the state. Unknown values fall back rather than rendering a blank page. */
export function readProgramQuery(params: URLSearchParams): ProgramQuery {
  const state = (params.get('state') ?? '').toUpperCase();
  const sort = params.get('sort');
  return {
    q: params.get('q') ?? '',
    state: STATE_NAMES.has(state) ? state : '',
    exam: params.get('exam') === 'masters' ? 'masters' : 'clinical',
    rated: params.get('rated') === '1',
    hbcu: params.get('hbcu') === '1',
    sort: isSort(sort) ? sort : PROGRAM_QUERY_DEFAULTS.sort,
    page: Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1),
  };
}

/** Defaults stay out of the URL, so the prerendered /programs keeps its bare canonical. */
export function programQueryToParams(query: ProgramQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q.trim()) params.set('q', query.q);
  if (query.state) params.set('state', query.state);
  if (query.exam !== PROGRAM_QUERY_DEFAULTS.exam) params.set('exam', query.exam);
  if (query.rated) params.set('rated', '1');
  if (query.hbcu) params.set('hbcu', '1');
  if (query.sort !== PROGRAM_QUERY_DEFAULTS.sort) params.set('sort', query.sort);
  if (query.page > 1) params.set('page', String(query.page));
  return params;
}
